import { useLayoutEffect, type ReactNode } from 'react';
import type { UserManagerSettings } from 'oidc-client-ts';
import { WorkspaceAuthProvider, registerFrontendAccessTokenAccessor, useFrontendAuth } from '@nwm-oss/auth-react';
import type { WorkspaceRuntimeMode } from '@nwm-oss/auth-react/runtime';

export function OidcOnlyWorkspaceAuthProvider({
  runtime,
  search,
  oidcSettings,
  oidcClientId,
  children,
}: {
  runtime: WorkspaceRuntimeMode;
  search: string;
  oidcSettings: UserManagerSettings;
  oidcClientId?: string;
  children: ReactNode;
}) {
  return (
    <WorkspaceAuthProvider
      runtime={runtime}
      search={search}
      authMode="oidc"
      oidcSettings={oidcSettings}
      oidcClientId={oidcClientId}
    >
      <AccessTokenRegistration>{children}</AccessTokenRegistration>
    </WorkspaceAuthProvider>
  );
}

/**
 * WorkspaceAuthProvider registers the token accessor in a passive effect, which React runs after
 * its children's effects. A page mounted in the same commit the token arrives (always the case for
 * the embedded console) therefore loaded with no token. Layout effects run before any passive one.
 */
export function AccessTokenRegistration({ children }: { children: ReactNode }) {
  const { accessToken } = useFrontendAuth();
  useLayoutEffect(() => {
    registerFrontendAccessTokenAccessor(() => accessToken);
  }, [accessToken]);
  return <>{children}</>;
}
